import { ParamIssueShortId, ParamIssueUrl, ParamOrganizationSlug, ParamRegionUrl } from "./schema-DKEjeVqP.js";
import { ApiError, ApiServerError } from "./errors-DG2FreDU.js";
import { defineTool } from "./define-SD6AQa4b.js";
import { apiServiceFromContext } from "./api-EaEPyKlQ.js";
import { parseIssueParams } from "./issue-helpers-0Dnme3Ym.js";
import { SEER_INITIAL_RETRY_DELAY, SEER_MAX_RETRIES, SEER_POLLING_INTERVAL, SEER_TIMEOUT, getActiveAutofixTodo, getHumanInterventionGuidance, getOutputForAutofixRun, getSeerUnsupportedIssueMessage, getStatusDisplayName, isSeerSupportedIssue, isTerminalStatus } from "./seer-BoTKAK7k.js";
import { assertIssueWithinProjectConstraint } from "./issue-D65xI0F7.js";
import { retryWithBackoff } from "./fetch-utils-By3YW-Mv.js";
import { z } from "zod";
import { setTag } from "@sentry/core";

//#region src/tools/catalog/analyze-issue-with-seer.ts
var analyze_issue_with_seer_default = defineTool({
	name: "analyze_issue_with_seer",
	skills: ["seer"],
	requiredScopes: [],
	description: [
		"Use Seer to analyze production errors and get detailed root cause analysis with specific code fixes.",
		"",
		"Use this tool when:",
		"- The user explicitly asks for root cause analysis, Seer analysis, or help fixing/debugging an issue",
		"- You are unable to accurately determine the root cause from the issue details alone",
		"",
		"Do NOT call this tool as an automatic follow-up to get_sentry_resource.",
		"",
		"What this tool provides:",
		"- Root cause analysis with code-level explanations",
		"- Specific file locations and line numbers where errors occur",
		"- Concrete code fixes you can apply",
		"- Step-by-step implementation guidance",
		"",
		"This tool automatically:",
		"1. Checks if analysis already exists (instant results)",
		"2. Starts new AI analysis if needed (~2-5 minutes)",
		"3. Returns complete fix recommendations",
		"",
		"<examples>",
		"### User: \"Run Seer on this issue\"",
		"",
		"```",
		"analyze_issue_with_seer(issueUrl='https://my-org.sentry.io/issues/PROJECT-1Z43')",
		"```",
		"",
		"### User: \"Analyze this issue and suggest a fix\"",
		"",
		"```",
		"analyze_issue_with_seer(organizationSlug='my-organization', issueId='ERROR-456')",
		"```",
		"</examples>",
		"",
		"<hints>",
		"- Only use when the user explicitly requests analysis or you cannot determine the root cause from issue details alone",
		"- Seer Autofix does not support metric alert issues (issueCategory: metric); use get_issue_details and search_events instead",
		"- If the user provides an issueUrl, extract it and use that parameter alone",
		"- The analysis includes actual code snippets and fixes, not just error descriptions",
		"- Results are cached - subsequent calls return instantly",
		"</hints>"
	].join("\n"),
	inputSchema: {
		organizationSlug: ParamOrganizationSlug.optional(),
		regionUrl: ParamRegionUrl.nullable().default(null),
		issueId: ParamIssueShortId.optional(),
		issueUrl: ParamIssueUrl.optional(),
		instruction: z.string().describe("Optional custom instruction for the AI analysis").optional()
	},
	annotations: {
		readOnlyHint: false,
		destructiveHint: false,
		openWorldHint: true
	},
	async handler(params, context) {
		const apiService = apiServiceFromContext(context, { regionUrl: params.regionUrl ?? void 0 });
		const { organizationSlug: orgSlug, issueId: parsedIssueId } = parseIssueParams({
			organizationSlug: params.organizationSlug,
			issueId: params.issueId,
			issueUrl: params.issueUrl
		});
		setTag("organization.slug", orgSlug);
		const issue = await apiService.getIssue({
			organizationSlug: orgSlug,
			issueId: parsedIssueId
		});
		assertIssueWithinProjectConstraint({
			issue,
			projectSlug: context.constraints.projectSlug
		});
		if (!isSeerSupportedIssue(issue)) return getSeerUnsupportedIssueMessage(issue);
		let output = `# Seer Analysis for Issue ${parsedIssueId}\n\n`;
		let autofixState = await retryWithBackoff(() => apiService.getAutofixState({
			organizationSlug: orgSlug,
			issueId: parsedIssueId
		}), {
			maxRetries: SEER_MAX_RETRIES,
			initialDelay: SEER_INITIAL_RETRY_DELAY,
			shouldRetry: (error) => {
				return error instanceof ApiServerError || !(error instanceof ApiError);
			}
		});
		if (!autofixState.autofix) {
			output += `Starting new analysis...\n\n`;
			const startResult = await apiService.startAutofix({
				organizationSlug: orgSlug,
				issueId: parsedIssueId,
				instruction: params.instruction
			});
			output += `Analysis started with Run ID: ${startResult.run_id}\n\n`;
			await new Promise((resolve) => setTimeout(resolve, 1e3));
			autofixState = await retryWithBackoff(() => apiService.getAutofixState({
				organizationSlug: orgSlug,
				issueId: parsedIssueId
			}), {
				maxRetries: SEER_MAX_RETRIES,
				initialDelay: SEER_INITIAL_RETRY_DELAY,
				shouldRetry: (error) => {
					return error instanceof ApiServerError || !(error instanceof ApiError);
				}
			});
		} else {
			output += `Found existing analysis (Run ID: ${autofixState.autofix.run_id})\n\n`;
			const existingStatus = autofixState.autofix.status;
			if (isTerminalStatus(existingStatus)) {
				output += `## Analysis ${getStatusDisplayName(existingStatus)}\n\n`;
				output += getOutputForAutofixRun(autofixState.autofix);
				if (existingStatus !== "completed") {
					output += `\n**Status**: ${existingStatus}\n`;
					output += getHumanInterventionGuidance(existingStatus);
					output += "\n";
				}
				return output;
			}
		}
		const startTime = Date.now();
		let lastStatus = "";
		let consecutiveErrors = 0;
		while (Date.now() - startTime < SEER_TIMEOUT) {
			if (!autofixState.autofix) {
				output += `Error: Analysis state lost. Please try again by running:\n`;
				output += `\`\`\`\n`;
				output += params.issueUrl ? `analyze_issue_with_seer(issueUrl="${params.issueUrl}")` : `analyze_issue_with_seer(organizationSlug="${orgSlug}", issueId="${parsedIssueId}")`;
				output += `\n\`\`\`\n`;
				return output;
			}
			const status = autofixState.autofix.status;
			if (isTerminalStatus(status)) {
				output += `## Analysis ${getStatusDisplayName(status)}\n\n`;
				output += getOutputForAutofixRun(autofixState.autofix);
				if (status !== "completed") {
					output += `\n**Status**: ${status}\n`;
					output += getHumanInterventionGuidance(status);
				}
				return output;
			}
			if (status !== lastStatus) {
				const activeTodo = getActiveAutofixTodo(autofixState.autofix);
				if (activeTodo) output += `Processing: ${activeTodo}...\n`;
				lastStatus = status;
			}
			await new Promise((resolve) => setTimeout(resolve, SEER_POLLING_INTERVAL));
			try {
				autofixState = await retryWithBackoff(() => apiService.getAutofixState({
					organizationSlug: orgSlug,
					issueId: parsedIssueId
				}), {
					maxRetries: SEER_MAX_RETRIES,
					initialDelay: SEER_INITIAL_RETRY_DELAY,
					shouldRetry: (error) => {
						return error instanceof ApiServerError || !(error instanceof ApiError);
					}
				});
				consecutiveErrors = 0;
			} catch (error) {
				consecutiveErrors++;
				if (consecutiveErrors >= 3) {
					output += `\n## Error During Analysis\n\n`;
					output += `Unable to retrieve analysis status after multiple attempts.\n`;
					output += `Error: ${error instanceof Error ? error.message : String(error)}\n\n`;
					output += `You can check the status later by running the same command again:\n`;
					output += `\`\`\`\n`;
					output += params.issueUrl ? `analyze_issue_with_seer(issueUrl="${params.issueUrl}")` : `analyze_issue_with_seer(organizationSlug="${orgSlug}", issueId="${parsedIssueId}")`;
					output += `\n\`\`\`\n`;
					return output;
				}
				output += `Temporary error retrieving status (attempt ${consecutiveErrors}/3), retrying...\n`;
			}
		}
		if (autofixState.autofix) {
			output += `**Current Status**: ${getStatusDisplayName(autofixState.autofix.status)}\n\n`;
			output += getOutputForAutofixRun(autofixState.autofix);
		}
		output += `\n## Analysis Timed Out\n\n`;
		output += `The analysis is taking longer than expected (>${SEER_TIMEOUT / 1e3}s).\n\n`;
		output += `\nYou can check the status later by running the same command again:\n`;
		output += `\`\`\`\n`;
		output += params.issueUrl ? `analyze_issue_with_seer(issueUrl="${params.issueUrl}")` : `analyze_issue_with_seer(organizationSlug="${orgSlug}", issueId="${parsedIssueId}")`;
		output += `\n\`\`\`\n`;
		return output;
	}
});

//#endregion
export { analyze_issue_with_seer_default };
//# sourceMappingURL=analyze-issue-with-seer-Dy01BWPf.js.map